import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import axios from "../../../api/axios";
import { ORDER_DETAILS_CONFIG_URL } from "../../../api/api_routing_urls";

import gratitudeLogo from "../../../assets/new_logo/gratitude_final_logo_white_nobg.png";
import AnimatedLoader from "../../../reusable-components/loader/animated_loader.component";
import { getFormattedDateDDMMMYYYYDate } from "../../../utilities/dateFunctions/formatdate";

const OrderSuccessPage = () => {
  const auth = useSelector((state) => state.authProvider.auth);
  const userId = auth?.user_id;

  const [isLoading, setIsLoading] = useState(true);
  const [orderDetailsClientList, setOrderDetailsClientList] = useState([]);
  const [fetchError, setFetchError] = useState("");

  const getOrderDetailsClientList = async (uid) => {
    try {
      setFetchError("");

      const response = await axios.get(ORDER_DETAILS_CONFIG_URL, {
        params: { user_id: uid },
      });

      console.log("Order details response:", response);

      if (response.status === 200) {
        const list = response?.data?.orderDetailsClientList ?? [];
        setOrderDetailsClientList(Array.isArray(list) ? list : []);
      } else if (response.status === 202) {
        setOrderDetailsClientList([]);
        console.log("Order details not found in the system.");
      } else {
        setOrderDetailsClientList([]);
      }
    } catch (error) {
      console.error(error);
      setOrderDetailsClientList([]);

      if (!error?.response) {
        setFetchError("No Server Response");
      } else if (error.response.status === 422) {
        setFetchError("Some of the required inputs were not provided.");
      } else {
        setFetchError(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      getOrderDetailsClientList(userId);
    } else {
      setIsLoading(false);
      setOrderDetailsClientList([]);
    }
  }, [userId]);

  const latestOrder = useMemo(() => {
    return orderDetailsClientList?.[0] ?? null;
  }, [orderDetailsClientList]);

  const orderedItems = latestOrder?.ordered_items ?? [];
  const billingAddress = latestOrder?.billing_address ?? null;

  return (
    <section className="font-tenorsans">
      <div className="bg-white min-h-screen pt-24 lg:pt-36 pb-20 px-3 w-[80%] mx-auto">
        <div className="bg-[#15274f] flex flex-col items-center justify-center">
          <img
            src={gratitudeLogo}
            className="mt-8 h-10 md:h-16"
            alt="Gratitude Logo"
          />

          <div className="my-16 text-white text-lg md:text-xl lg:text-3xl">
            Order Confirmation
          </div>
        </div>

        <div className="mt-10 text-sm md:text-base">
          {auth ? (
            <div>
              Hello {auth?.first_name} {auth?.last_name},
            </div>
          ) : (
            <div>Dear Customer,</div>
          )}
        </div>

        <div className="mt-5 text-sm md:text-base">
          Thank you for your purchase. A record of your purchase information
          appears below.
        </div>

        <div className="mt-14">
          <div className="uppercase text-lg font-semibold">
            Order Information
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white h-[40vh] flex items-center justify-center">
            <AnimatedLoader />
          </div>
        ) : fetchError ? (
          <div className="mt-8 border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {fetchError}
          </div>
        ) : !latestOrder ? (
          <div className="mt-8 border border-gray-200 bg-gray-50 px-4 py-6">
            <div className="text-sm md:text-base">
              Your order was placed successfully.
            </div>
            <div className="mt-2 text-xs md:text-sm text-gray-600">
              We could not load the order summary right now.
            </div>

            <Link to="/">
              <div className="mt-6 inline-flex cursor-pointer bg-white text-black text-xs border border-black hover:bg-black hover:text-white transition-all ease-in-out duration-300 px-3 py-2 lg:px-5 lg:py-3 uppercase">
                Continue Shopping
              </div>
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-5 mb-20">
              <div>
                <div className="flex flex-col gap-2 md:flex-row md:justify-between text-xs md:text-sm lg:text-lg">
                  <div>Order# {latestOrder?.order_no || "N/A"}</div>

                  <div>
                    Order Placed on{" "}
                    {latestOrder?.order_date
                      ? getFormattedDateDDMMMYYYYDate(latestOrder.order_date)
                      : "N/A"}
                  </div>
                </div>

                <div className="mt-2 text-xs md:text-sm lg:text-lg">
                  {latestOrder?.payment_mode === "COD" ? (
                    <>
                      Payment Mode: <span className="font-medium">Cash on Delivery</span>
                    </>
                  ) : (
                    <>
                      Amount Paid (Incl. delivery): ₹{" "}
                      {orderedItems?.[0]?.product_details?.amount_paid ??
                        latestOrder?.order_total ??
                        "N/A"}
                    </>
                  )}
                </div>

                <div className="text-gray-500 font-light text-xs lg:text-sm pt-3">
                  Order Items:
                </div>
              </div>

              {orderedItems.length > 0 ? (
                orderedItems.map((itemObj, index) => (
                  <div
                    key={itemObj?.order_details_id || `${latestOrder?.order_id}-${index}`}
                    className="my-3"
                  >
                    <OrderedItemCard
                      order_id={latestOrder?.order_id}
                      order_no={latestOrder?.order_no}
                      order_details_id={itemObj?.order_details_id}
                      order_status={latestOrder?.order_status}
                      is_return_initiated={itemObj?.is_return_initiated}
                      awb_tracking_number={latestOrder?.awb_tracking_number || null}
                      order_date={latestOrder?.order_date}
                      billing_address={latestOrder?.billing_address}
                      company_name="Gratitude"
                      product_image={itemObj?.product_image}
                      product_id={itemObj?.product_details?.product_id}
                      product_name={itemObj?.product_details?.product_name}
                      product_size={itemObj?.product_details?.size_name}
                      product_rate={itemObj?.product_details?.product_rate}
                      discount_percentage={
                        itemObj?.product_details?.discount_percentage
                      }
                      quantity={itemObj?.product_details?.quantity}
                      amount_paid={itemObj?.product_details?.amount_paid}
                    />
                  </div>
                ))
              ) : (
                <div className="mt-4 text-sm text-gray-600">
                  No order items found.
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col lg:flex-row lg:justify-between gap-10">
              <AddressBlock title="Shipping Address" billingAddress={billingAddress} />
              <AddressBlock title="Billing Address" billingAddress={billingAddress} />
            </div>

            <Link to="/">
              <div className="mt-16 flex justify-center cursor-pointer">
                <div className="lg:mt-5 mb-20 bg-white text-black text-xs border-[0.1px] border-black hover:bg-black hover:text-white transition-all ease-in-out duration-300 px-3 py-2 lg:px-5 lg:py-3 uppercase">
                  Continue Shopping
                </div>
              </div>
            </Link>
          </>
        )}
      </div>
    </section>
  );
};

export default OrderSuccessPage;

const AddressBlock = ({ title, billingAddress }) => {
  return (
    <div className="flex flex-col gap-y-2">
      <div className="font-bold">{title}</div>
      <div>
        <p className="font-semibold text-sm md:text-base lg:text-lg">
          {billingAddress?.first_name || ""} {billingAddress?.last_name || ""}
        </p>

        <div className="text-xs md:text-sm lg:text-base">
          <p>{billingAddress?.address_line1 || "N/A"}</p>
          <p>{billingAddress?.address_line2 || "N/A"}</p>
          <p>{billingAddress?.landmark || "N/A"}</p>
          <p>{billingAddress?.city_name || "N/A"}</p>
          <p>
            {billingAddress?.state_name || "N/A"} -{" "}
            {billingAddress?.pincode || "N/A"}
          </p>
          <p className="mt-2">
            <strong>Contact Email:</strong>{" "}
            {billingAddress?.user_email || "N/A"}
          </p>
          <p>
            <strong>Phone Number:</strong>{" "}
            {billingAddress?.phone_number || "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
};

const OrderedItemCard = ({
  order_no,
  order_date,
  product_image,
  product_name,
  product_size,
  product_rate,
  discount_percentage,
  quantity,
}) => {
  const itemTotal =
    Number(discount_percentage) === 0
      ? Number(quantity || 0) * Number(product_rate || 0)
      : Number(quantity || 0) *
        (Number(product_rate || 0) -
          (Number(discount_percentage || 0) / 100) * Number(product_rate || 0));

  return (
    <div className="border border-black font-tenorsans">
      <div className="flex justify-between bg-black px-3 py-4">
        <div className="flex flex-col md:flex-row gap-5 md:gap-20">
          <div className="flex-col">
            <div className="uppercase text-xs text-white">Order Date</div>
            <div className="text-xs text-white">
              {order_date ? getFormattedDateDDMMMYYYYDate(order_date) : "N/A"}
            </div>
          </div>

          <div className="flex-col">
            <div className="uppercase text-xs text-white">Item Total</div>
            <div className="text-xs text-white">₹ {itemTotal}</div>
          </div>
        </div>

        <div className="uppercase text-xs text-white">
          Order# <span className="text-white">{order_no || "N/A"}</span>
        </div>
      </div>

      <div className="bg-white px-3 py-5">
        <div className="pt-8 grid grid-cols-5 lg:pt-5">
          <div className="col-span-2 md:col-span-1">
            <img
              src={product_image}
              className="w-20 md:w-28 lg:w-40"
              alt={product_name || "Product"}
            />
          </div>

          <div className="md:ml-5 lg:ml-0 col-span-3 md:col-span-4">
            <div className="flex-col">
              <span className="text-xs lg:text-base text-black">
                {product_name || "N/A"}
              </span>

              <div className="text-xs lg:text-sm">
                Size: <span>{product_size || "N/A"}</span>
              </div>
              <div className="text-xs lg:text-sm">
                Quantity: <span>{quantity || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};